// React is imported implicitly by JSX transform
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Sparkles, Wand2, CheckCircle } from 'lucide-react';

/**
 * GlobalAISuggestionBanner Component
 *
 * A prominent banner that allows users to apply ALL AI suggestions at once.
 * Displays a summary of what will be applied and a single "Apply" button.
 *
 * Props:
 * - aiSuggestion: Complete AI suggestion object with template, effects, style, texts
 * - onApply: Callback when user clicks apply
 * - isApplied: Whether AI suggestions are currently applied (all values match)
 * - disabled: Disable the apply button
 */
export const GlobalAISuggestionBanner = ({
  aiSuggestion = null,
  onApply,
  isApplied = false,
  disabled = false,
}) => {
  const { t } = useTranslation('approval');

  // Don't render if no AI suggestion available
  if (!aiSuggestion) {
    return null;
  }

  // Count active effects
  const activeEffectsCount = aiSuggestion.effects
    ? Object.values(aiSuggestion.effects).filter(Boolean).length
    : 0;

  // Get template name for display
  const templateNames = {
    MinimalistReveal: 'Minimalist Reveal',
    UrgencyFlashSale: 'Flash Sale',
    SocialProofShowcase: 'Social Proof',
    FeatureDemoFlow: 'Feature Demo',
  };
  const templateName = templateNames[aiSuggestion.templateId] || aiSuggestion.templateId;

  const handleApply = () => {
    if (disabled || isApplied) return;
    if (onApply) {
      onApply(aiSuggestion);
    }
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl
        ${isApplied
          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200'
          : 'bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 border-2 border-purple-200'
        }
        transition-all duration-300
      `}
    >
      {/* Subtle animated background pattern */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, ${isApplied ? '#10b981' : '#8b5cf6'}15 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, ${isApplied ? '#10b981' : '#6366f1'}15 0%, transparent 50%)`,
        }}
      />

      <div className="relative p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={`
              flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
              ${isApplied
                ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                : 'bg-gradient-to-br from-purple-500 to-indigo-600'
              }
              shadow-lg
            `}
          >
            {isApplied ? (
              <CheckCircle size={20} className="text-white" />
            ) : (
              <Wand2 size={20} className="text-white" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4
                className={`
                  text-sm font-semibold
                  ${isApplied ? 'text-green-800' : 'text-purple-800'}
                `}
              >
                {isApplied
                  ? t('aiSuggestion.applied', 'KI-Empfehlung angewendet')
                  : t('aiSuggestion.title', 'KI-Empfehlung verfügbar')
                }
              </h4>
              <Sparkles
                size={14}
                className={isApplied ? 'text-green-500' : 'text-purple-500'}
              />
            </div>

            {/* Summary */}
            <p
              className={`
                text-xs mb-2
                ${isApplied ? 'text-green-700' : 'text-purple-700'}
              `}
            >
              {isApplied
                ? t('aiSuggestion.appliedDescription', 'Alle KI-Vorschläge wurden übernommen.')
                : t('aiSuggestion.description', 'Template, Effekte & Stil auf Knopfdruck übernehmen.')
              }
            </p>

            {/* Details chips */}
            <div className="flex flex-wrap gap-1.5">
              {aiSuggestion.templateId && (
                <span
                  className={`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                    ${isApplied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-purple-100 text-purple-700'
                    }
                  `}
                >
                  🎬 {templateName}
                </span>
              )}
              {activeEffectsCount > 0 && (
                <span
                  className={`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                    ${isApplied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-purple-100 text-purple-700'
                    }
                  `}
                >
                  ✨ {activeEffectsCount} {t('aiSuggestion.effects', 'Effekte')}
                </span>
              )}
              {aiSuggestion.style?.backgroundColor && (
                <span
                  className={`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                    ${isApplied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-purple-100 text-purple-700'
                    }
                  `}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-white shadow-sm"
                    style={{ backgroundColor: aiSuggestion.style.backgroundColor }}
                  />
                  {t('aiSuggestion.style', 'Stil')}
                </span>
              )}
            </div>

            {/* Reasoning (if provided) */}
            {aiSuggestion.reasoning && !isApplied && (
              <p className="mt-2 text-xs text-purple-600 italic line-clamp-2">
                &ldquo;{aiSuggestion.reasoning}&rdquo;
              </p>
            )}
          </div>

          {/* Apply Button */}
          {!isApplied && (
            <button
              type="button"
              onClick={handleApply}
              disabled={disabled}
              className={`
                flex-shrink-0 px-4 py-2 rounded-lg
                bg-gradient-to-r from-purple-600 to-indigo-600
                hover:from-purple-700 hover:to-indigo-700
                text-white text-sm font-semibold
                shadow-md hover:shadow-lg
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                disabled:hover:shadow-md
              `}
            >
              {t('aiSuggestion.applyButton', 'Anwenden')}
            </button>
          )}

          {/* Applied checkmark */}
          {isApplied && (
            <div className="flex-shrink-0 flex items-center gap-1 px-3 py-2 text-green-700 text-sm font-medium">
              <CheckCircle size={16} />
              {t('aiSuggestion.appliedBadge', 'Aktiv')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

GlobalAISuggestionBanner.propTypes = {
  aiSuggestion: PropTypes.shape({
    templateId: PropTypes.string,
    effects: PropTypes.object,
    style: PropTypes.shape({
      backgroundColor: PropTypes.string,
      captionColor: PropTypes.string,
      accentColor: PropTypes.string,
    }),
    texts: PropTypes.shape({
      headline: PropTypes.string,
      caption: PropTypes.string,
      ctaText: PropTypes.string,
    }),
    reasoning: PropTypes.string,
  }),
  onApply: PropTypes.func,
  isApplied: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default GlobalAISuggestionBanner;
